import { useCallback, useEffect, useState } from 'react';

import { EMPTY_ARRAY, EMPTY_FUNCTION } from '../../../constants';

interface KeyboardNavigationProps<T> {
  selected_index: number;
  keyboard_disabled?: boolean;
  options: T[];
  onKeyDown: (e: React.KeyboardEvent<HTMLElement>) => void;
  onSelect: (option: T) => void;
  onClose: () => void;
  is_open: boolean;
}

export const useKeyboardNavigation = <T extends Record<string, any>>({
  selected_index,
  keyboard_disabled = false,
  options = EMPTY_ARRAY,
  onKeyDown: onKeyDownProp = EMPTY_FUNCTION,
  onSelect = EMPTY_FUNCTION,
  onClose = EMPTY_FUNCTION,
  is_open,
}: KeyboardNavigationProps<T>) => {
  const [keyboard_index, setKeyboardIndex] = useState(-1);

  useEffect(() => {
    if (!is_open || options.length === 0 || keyboard_disabled) {
      setKeyboardIndex(-1);
      return;
    }

    if (selected_index >= 0 && selected_index < options.length) {
      setKeyboardIndex(selected_index);
    } else {
      setKeyboardIndex(-1);
    }
  }, [is_open, keyboard_disabled, selected_index, options]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLElement>) => {
      onKeyDownProp(e);

      if (keyboard_disabled) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!is_open) {
            onClose();
            return;
          }
          setKeyboardIndex((prev) => {
            if (prev === -1) return 0;
            return prev < options.length - 1 ? prev + 1 : 0;
          });
          break;

        case 'ArrowUp':
          e.preventDefault();
          if (!is_open) {
            onClose();
            return;
          }
          setKeyboardIndex((prev) => {
            if (prev === -1) return options.length - 1;
            return prev > 0 ? prev - 1 : options.length - 1;
          });
          break;

        case 'Enter':
          e.preventDefault();
          if (keyboard_index !== -1 && keyboard_index < options.length) {
            onSelect(options[keyboard_index]);
            setKeyboardIndex(-1);
          }
          break;

        case 'Escape':
          e.preventDefault();
          setKeyboardIndex(-1);
          onClose();
          break;

        case 'Tab':
          setKeyboardIndex(-1);
          onClose();
          break;

        default:
          if (e.key.length === 1) {
            setKeyboardIndex(-1);
          }
          break;
      }
    },
    [onKeyDownProp, options, keyboard_disabled, is_open, keyboard_index, onClose, onSelect]
  );

  useEffect(() => {
    if (keyboard_index >= options.length) {
      setKeyboardIndex(-1);
    }
  }, [keyboard_index, options.length]);

  return { keyboard_index, setKeyboardIndex, onKeyDown };
};
